import _defineProperty from '@babel/runtime/helpers/defineProperty';
import styled, { keyframes, css } from 'styled-components';
import { siteThemes, theme, isGlobetrotter } from '../../utils/theming.js';
import mq from '../../utils/mediaQueries.js';

var GLOBETROTTER = siteThemes.GLOBETROTTER,
  DEFAULT = siteThemes.DEFAULT;
var filledButtonTypes = ['primary', 'tertiary'];
var getMainColor = function getMainColor(state) {
  var potentialState = state ? "-".concat(state) : '';
  return function (_ref) {
    var $variant = _ref.$variant;
    return css(["var(--color-cta-button-", "", ")"], $variant, potentialState);
  };
};
var getFilledButtonTextColor = function getFilledButtonTextColor() {
  return function (_ref2) {
    var $variant = _ref2.$variant,
      site = _ref2.theme.site;
    if ($variant === 'tertiary' && site === GLOBETROTTER) {
      return css(["var(--color-black)"]);
    }
    return css(["var(--color-white)"]);
  };
};
var borderRadius = theme('site', _defineProperty(_defineProperty({}, DEFAULT, '25px'), GLOBETROTTER, '0'));
var borderWidth = theme('site', _defineProperty(_defineProperty({}, DEFAULT, '2px'), GLOBETROTTER, '1px'));
var letterSpacing = theme('site', _defineProperty(_defineProperty({}, DEFAULT, '0.5px'), GLOBETROTTER, '1.4px'));
var getPadding = function getPadding() {
  return function (_ref3) {
    var $variant = _ref3.$variant;
    if (filledButtonTypes.includes($variant)) {
      return theme('site', _defineProperty(_defineProperty({}, DEFAULT, function (props) {
        return props.$size === 's' ? css(["var(--space-100) var(--space-400)"]) : css(["var(--space-200) var(--space-500)"]);
      }), GLOBETROTTER, function (props) {
        return props.$size === 's' ? css(["var(--space-100) var(--space-300)"]) : css(["var(--space-300) var(--space-500)"]);
      }));
    }
    return theme('site', _defineProperty(_defineProperty({}, DEFAULT, function (props) {
      return props.$size === 's' ? css(["calc(var(--space-100) - ", ") calc(var(--space-400) - ", ")"], borderWidth, borderWidth) : css(["calc(var(--space-200) - ", ") calc(var(--space-500) - ", ")"], borderWidth, borderWidth);
    }), GLOBETROTTER, function (props) {
      return props.$size === 's' ? css(["calc(var(--space-100) - ", ") calc(var(--space-300) - ", ")"], borderWidth, borderWidth) : css(["calc(var(--space-300) - ", ") calc(var(--space-500) - ", ")"], borderWidth, borderWidth);
    }));
  };
};
var buttonLoader = keyframes(["from{transform:rotate(0deg);}to{transform:rotate(360deg);}"]);
var Button = styled.button.withConfig({
  displayName: "Buttonstyle__Button",
  componentId: "sc-1icf9nl-0"
})(["display:inline-flex;align-items:center;justify-content:center;border:", " solid ", ";border-radius:", ";color:", ";max-width:100%;width:fit-content;letter-spacing:", ";padding:", ";text-transform:uppercase;transition:border-color var(--transition),box-shadow var(--transition),color var(--transition),background var(--transition);", " &:hover{box-shadow:0px 1px 1.5px 0px rgba(2,3,3,0.3);color:", ";border-color:", ";", "}&:active{box-shadow:none;color:", ";border-color:", ";}", " ", " ", " ", " &::before{display:none;", " ", "}&:focus-visible{border-radius:", ";}"], getMainColor(), borderWidth, borderRadius, getMainColor(), letterSpacing, getPadding(), mq.sMax(css(["", ""], function (_ref4) {
  var $fullWidthMobile = _ref4.$fullWidthMobile;
  return $fullWidthMobile && css(["width:100%;"]);
})), getMainColor('hover'), getMainColor('hover'), isGlobetrotter(css(["", ""], function (_ref5) {
  var $variant = _ref5.$variant;
  return filledButtonTypes.includes($variant) && css(["box-shadow:none;"]);
})), getMainColor('active'), getMainColor('active'), function (_ref6) {
  var $variant = _ref6.$variant;
  return filledButtonTypes.includes($variant) && css(["background:", ";border:none;color:", ";&:hover{background:", ";color:", ";}&:active{background:", ";color:", ";}"], getMainColor(), getFilledButtonTextColor(), getMainColor('hover'), getFilledButtonTextColor(), getMainColor('active'), getFilledButtonTextColor());
}, function (_ref7) {
  var $variant = _ref7.$variant;
  return $variant === 'ghost' && css(["border-color:transparent;&:active,&:hover{border-color:var(--color-cta-button-ghost);}"]);
}, function (props) {
  return (props.$loading || props.$disabled) && css(["cursor:default;user-select:none;pointer-events:none;"]);
}, function (props) {
  return props.$disabled && css(["background:var(--color-background-disabled);border-color:var(--color-background-disabled);color:var(--color-text);"]);
}, function (props) {
  return props.$loading && css(["animation-duration:1s;animation-iteration-count:infinite;animation-name:", ";animation-timing-function:linear;border:3px solid transparent;border-radius:50%;border-top:3px solid ", ";content:'';display:inline-block;height:12px;width:12px;position:absolute;"], buttonLoader, function (_ref8) {
    var $variant = _ref8.$variant;
    return filledButtonTypes.includes($variant) ? getFilledButtonTextColor() : getMainColor();
  });
}, function (props) {
  return props.$disabled && props.$loading && css(["border-top-color:var(--color-white);"]);
}, borderRadius);

/**
 * @typedef {{
 *  $loading?: boolean,
 *  $right?: boolean
 * }} InternalIconProps
 *
 * @typedef {import('styled-components').IStyledComponent<
 *  'web',
 *  import('react').DetailedHTMLProps<import('react').HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> & InternalIconProps
 * >} IconComponent
 *
 * @type {IconComponent}
 */
var Icon = styled.span.withConfig({
  displayName: "Buttonstyle__Icon",
  componentId: "sc-1icf9nl-1"
})(["margin:0 var(--space-200) 0 0;line-height:0;svg{height:1.4rem;width:1.4rem;}", " ", ""], function (_ref9) {
  var $loading = _ref9.$loading;
  return $loading && css(["visibility:hidden;"]);
}, function (_ref10) {
  var $right = _ref10.$right;
  return $right && css(["margin:0 0 0 var(--space-200);"]);
});

/**
 * @typedef {{
 *  $loading?: boolean,
 * }} InternalTextProps
 *
 * @typedef {import('styled-components').IStyledComponent<
 *  'web',
 *  import('react').DetailedHTMLProps<import('react').HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> & InternalIconProps
 * >} TextComponent
 *
 * @type {TextComponent}
 */
var Text = styled.span.withConfig({
  displayName: "Buttonstyle__Text",
  componentId: "sc-1icf9nl-2"
})(["text-overflow:ellipsis;overflow:hidden;white-space:nowrap;font:var(--font-button);", " ", ""], isGlobetrotter(css(["line-height:1.6;"])), function (props) {
  return props.$loading && css(["visibility:hidden;"]);
});

export { Button, Icon, Text };
//# sourceMappingURL=Button.style.js.map
